import { useEffect, useRef } from "react";
import {
    Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
    Link2, Heading2, Heading3, MousePointerClick, ImagePlus, CornerDownLeft,
} from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";

/**
 * RichTextEditor — lightweight WYSIWYG based on contentEditable +
 * document.execCommand. Emits sanitized HTML via `onChange`.
 *
 * Toolbar covers the formatting listed in the CRM spec:
 *   • H2 / H3
 *   • Bold / Italic / Underline
 *   • Bullet + numbered lists
 *   • Links
 *   • CTA button (styled inline)
 *   • Line break (Shift+Enter shortcut also works)
 *
 * Consumer is responsible for template-token substitution ({{fornavn}} etc.)
 * — they pass through the editor as plain text and are preserved verbatim.
 */
export default function RichTextEditor({ value, onChange, testid }) {
    const ref = useRef(null);
    // Use a sentinel so the first useEffect run ALWAYS writes to the DOM,
    // even if `value` equals the empty string.
    const lastValueRef = useRef(null);

    // Sync external value → editor DOM only when it truly differs.
    // Skips writes triggered by our own onInput so caret position is kept.
    // DOMPurify runs defence-in-depth on the incoming value so a malicious
    // stored payload (e.g. from a compromised DB entry) can't execute in
    // the admin's browser when the flow editor loads it.
    useEffect(() => {
        if (!ref.current) return;
        if (value === lastValueRef.current) return;
        const safe = sanitizeHtml(value || "");
        ref.current.innerHTML = safe;
        lastValueRef.current = safe;
    }, [value]);

    const emit = () => {
        const html = ref.current?.innerHTML || "";
        lastValueRef.current = html;
        onChange?.(html);
    };

    const exec = (cmd, arg = null) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        emit();
    };

    const insertLink = () => {
        const url = window.prompt("Indsæt URL (fx https://cleany.dk)");
        if (!url) return;
        exec("createLink", url);
        // Post-hoc: ensure target="_blank" for external links
        setTimeout(() => {
            ref.current?.querySelectorAll("a").forEach((a) => {
                if (!a.getAttribute("target")) {
                    a.setAttribute("target", "_blank");
                    a.setAttribute("rel", "noopener");
                }
            });
            emit();
        }, 0);
    };

    const insertButton = () => {
        const label = window.prompt("Knap-tekst (fx 'Book gennemgang')");
        if (!label) return;
        const url = window.prompt("URL som knappen skal føre til (fx {{booking_link}})");
        if (!url) return;
        const html = `<p><a href="${url}" style="display:inline-block;background:#0284c7;color:#fff;text-decoration:none;padding:12px 22px;border-radius:8px;font-weight:600;font-family:Arial,sans-serif;">${label}</a></p>`;
        exec("insertHTML", html);
    };

    const insertLineBreak = () => exec("insertHTML", "<br>");

    const onKeyDown = (e) => {
        // Convert plain-text pastes and prevent messy formatting from browser default
        if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            insertLineBreak();
        }
    };

    const onPaste = (e) => {
        // Strip formatting on paste to avoid Word/Docs bloat.
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
        emit();
    };

    const Btn = ({ onClick, icon: Icon, label, testid: t }) => (
        <button
            type="button"
            onClick={onClick}
            title={label}
            data-testid={t}
            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors"
        >
            <Icon className="h-3.5 w-3.5" />
        </button>
    );

    return (
        <div className="rounded-lg border border-slate-300 bg-white overflow-hidden" data-testid={testid}>
            <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border-b border-slate-200">
                <Btn onClick={() => exec("formatBlock", "H2")} icon={Heading2} label="Overskrift stor" testid="rte-h2" />
                <Btn onClick={() => exec("formatBlock", "H3")} icon={Heading3} label="Overskrift lille" testid="rte-h3" />
                <Btn onClick={() => exec("formatBlock", "P")} icon={CornerDownLeft} label="Almindelig tekst" testid="rte-p" />
                <span className="w-px bg-slate-300 mx-1" />
                <Btn onClick={() => exec("bold")} icon={Bold} label="Fed" testid="rte-bold" />
                <Btn onClick={() => exec("italic")} icon={Italic} label="Kursiv" testid="rte-italic" />
                <Btn onClick={() => exec("underline")} icon={UnderlineIcon} label="Understreg" />
                <span className="w-px bg-slate-300 mx-1" />
                <Btn onClick={() => exec("insertUnorderedList")} icon={List} label="Punkter" testid="rte-ul" />
                <Btn onClick={() => exec("insertOrderedList")} icon={ListOrdered} label="Nummerering" testid="rte-ol" />
                <span className="w-px bg-slate-300 mx-1" />
                <Btn onClick={insertLink} icon={Link2} label="Link" testid="rte-link" />
                <Btn onClick={insertButton} icon={MousePointerClick} label="Knap (CTA)" testid="rte-cta" />
                <Btn onClick={insertLineBreak} icon={ImagePlus} label="Linjeskift (Shift+Enter)" testid="rte-br" />
            </div>
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={emit}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                data-testid={`${testid}-area`}
                className="min-h-[200px] max-h-[500px] overflow-y-auto p-4 text-sm text-slate-800 focus:outline-none prose prose-sm max-w-none"
                style={{ lineHeight: 1.6 }}
            />
            <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
                Tip: Enter starter et nyt afsnit · Shift+Enter laver et blødt linjeskift · Marker tekst og klik <b>B</b> eller <b>I</b> for formatering
            </div>
        </div>
    );
}
